/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import {
  FiClock,
  FiHeart,
  FiMail,
  FiMapPin,
  FiPhoneCall,
  FiMenu,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cleanup body class when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove("mobile-menu-open");
      document.documentElement.classList.remove("mobile-menu-open");
    };
  }, []);

  const toggleNavbar = () => {
    const newState = !isOpen;
    setIsOpen(newState);

    // Prevent body scroll when mobile menu is open
    if (newState) {
      document.body.classList.add("mobile-menu-open");
      document.documentElement.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
      document.documentElement.classList.remove("mobile-menu-open");
    }
  };

  return (
    <>
      {isOpen && <MobileNavbar toggleNavbar={toggleNavbar} />}
      {/* Top Bar */}
      <div className="hidden md:block sticky top-0 z-[100] overflow-hidden bg-[rgb(4,50,75)] text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 text-sm">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <FiMail className="h-4 w-4 text-[rgb(210,166,73)]" />
              <a
                href="mailto:info@acdha.org"
                className="transition-colors hover:text-[rgb(210,166,73)]"
              >
                info@acdha-ss.org
              </a>
            </div>
            <div className="flex items-center space-x-2">
              <FiPhoneCall className="h-4 w-4 text-[rgb(210,166,73)]" />
              <a
                href="tel:+2119124467697"
                className="transition-colors hover:text-[rgb(210,166,73)]"
              >
                +211 912 446 7697
              </a>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-xs">Office Hours: Mon-Fri 8AM-5PM</span>
            <div className="h-4 w-px bg-gray-400"></div>
            <span className="text-xs">📍 Juba, South Sudan</span>
            <div className="h-4 w-px bg-gray-400"></div>
            <TopBarLanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`sticky top-0 md:top-[41px] z-50 w-full overflow-visible ${
          scrolled
            ? "border-b border-gray-200 bg-white/95 shadow-lg backdrop-blur-md"
            : "bg-white"
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex min-w-0 items-center justify-between">
            {/* Logo */}
            <div className="gpu-accelerated flex min-w-0 flex-shrink-0 items-center space-x-3">
              <div className="relative">
                <img
                  src="/images/logo1.png"
                  className="h-12 w-12 rounded-lg shadow-md md:h-14 md:w-14"
                  alt="ACDHA Logo"
                />
              </div>
              <div className="block min-w-0">
                <h1 className="truncate text-lg font-bold text-[rgb(4,50,75)] md:text-xl">
                  AFRICAN COOPERATION
                </h1>
                <p className="truncate text-[10px] text-gray-600 md:text-sm">
                  FOR DEVELOPMENT AND HUMANITARIAN AID
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden flex-shrink-0 items-center space-x-8 lg:flex">
              {navLinks.map((link) => (
                <div key={link.text}>
                  <Link
                    to={link.path}
                    className="group relative font-medium text-[rgb(4,50,75)] transition-colors hover:text-[rgb(210,166,73)]"
                  >
                    {t(link.text)}
                    <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[rgb(210,166,73)] transition-all group-hover:w-full"></span>
                  </Link>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden flex-shrink-0 items-center space-x-4 lg:flex relative">
              <div className="gpu-accelerated">
                <Link
                  to="/donate"
                  className="group relative inline-flex items-center space-x-2 rounded-full bg-gradient-to-r from-[rgb(210,166,73)] to-[rgb(246,203,110)] px-6 py-3 text-sm font-semibold text-[rgb(4,50,75)] shadow-lg hover:shadow-xl"
                >
                  <FiHeart className="h-4 w-4" />
                  <span>{t("navbar.donate")}</span>
                  <div className="absolute inset-0 rounded-full bg-white/20 opacity-0 transition-opacity group-hover:opacity-100"></div>
                </Link>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleNavbar}
              className="gpu-accelerated rounded-lg p-2 text-[rgb(4,50,75)] transition-colors hover:bg-gray-100 lg:hidden"
            >
              <FiMenu className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Bar */}
        <div className="overflow-hidden border-t border-gray-200 bg-gray-50 lg:hidden">
          <div className="mx-auto max-w-7xl px-4 py-3">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center space-x-2 sm:space-x-4">
                {navLinks
                  .filter((link) => link.text !== "navbar.ourWork")
                  .slice(0, 2)
                  .map((link) => (
                    <Link
                      key={link.text}
                      to={link.path}
                      className="truncate text-xs sm:text-sm font-medium text-[rgb(4,50,75)] transition-colors hover:text-[rgb(210,166,73)] px-2 py-1 rounded-md hover:bg-white/50"
                    >
                      {t(link.text)}
                    </Link>
                  ))}
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <MobileLanguageSwitcher />
                <Link
                  to="/donate"
                  className="flex-shrink-0 rounded-full bg-[rgb(210,166,73)] px-3 py-2 text-xs font-semibold text-[rgb(4,50,75)] shadow-md hover:shadow-lg"
                >
                  {t("navbar.donate")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

// Top Bar Language Switcher Component
function TopBarLanguageSwitcher() {
  const { i18n } = useTranslation();

  const languages = [
    { code: "en", name: "EN", flag: "🇺🇸" },
    { code: "ar", name: "ع", flag: "🇸🇦" },
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Update document direction for RTL support
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = langCode;
  };

  return (
    <div className="flex items-center space-x-1">
      {languages.map((language) => (
        <button
          key={language.code}
          onClick={() => changeLanguage(language.code)}
          className={`flex items-center space-x-1 rounded-md px-2 py-1 text-xs font-medium ${
            i18n.language === language.code
              ? "bg-[rgb(210,166,73)] text-[rgb(4,50,75)]"
              : "text-gray-300 hover:bg-white/10 hover:text-white"
          }`}
        >
          <span className="text-sm">{language.flag}</span>
          <span>{language.name}</span>
        </button>
      ))}
    </div>
  );
}

// Mobile Language Switcher Component
function MobileLanguageSwitcher() {
  const { i18n } = useTranslation();

  const languages = [
    { code: "en", name: "EN", flag: "🇺🇸" },
    { code: "ar", name: "ع", flag: "🇸🇦" },
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Update document direction for RTL support
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = langCode;
  };

  return (
    <div className="flex items-center space-x-0.5 rounded-md border border-gray-200 bg-white p-0.5">
      {languages.map((language) => (
        <button
          key={language.code}
          onClick={() => changeLanguage(language.code)}
          className={`flex items-center justify-center rounded px-2 py-1 text-xs font-medium min-w-[2.5rem] ${
            i18n.language === language.code
              ? "bg-[rgb(210,166,73)] text-[rgb(4,50,75)]"
              : "text-gray-600 hover:bg-gray-50"
          }`}
          title={language.code === "en" ? "English" : "العربية"}
        >
          <span className="text-xs">{language.flag}</span>
          <span className="ml-1 hidden sm:inline">{language.name}</span>
        </button>
      ))}
    </div>
  );
}

const navLinks = [
  {
    text: "navbar.home",
    path: "/",
  },
  {
    text: "navbar.about",
    path: "/about",
  },
  {
    text: "navbar.ourWork",
    path: "/our-work",
  },
  {
    text: "navbar.volunteer",
    path: "/get-involved",
  },
  {
    text: "navbar.contact",
    path: "/contact",
  },
];

function MobileNavbar({ toggleNavbar }) {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] flex h-screen w-screen max-h-screen flex-col overflow-hidden bg-white"
      style={{ touchAction: "none" }}
    >
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <img
              src="/images/logo1.png"
              className="h-8 w-8 rounded-lg shadow-md"
              alt="ACDHA Logo"
            />
          </div>
          <div>
            <p className="text-xs font-bold text-[rgb(4,50,75)]">ACDHA</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <TopBarLanguageSwitcher />
          <button
            onClick={toggleNavbar}
            className="rounded-full bg-gray-100 p-1.5 text-gray-600 transition-colors hover:bg-gray-200"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Navigation Content - Fixed, no scroll */}
      <div className="flex flex-1 flex-col overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        {/* Main Navigation */}
        <div className="flex flex-1 flex-col justify-center px-4 py-4">
          <ul className="space-y-2">
            {navLinks
              .filter((link) => link.text !== "navbar.contact")
              .map((link) => (
                <li key={link.text}>
                  <Link
                    className="group flex items-center justify-between rounded-xl bg-white px-4 py-3 font-medium text-[rgb(4,50,75)] shadow-sm hover:bg-[rgb(210,166,73)] hover:text-white hover:shadow-md"
                    onClick={toggleNavbar}
                    to={link.path}
                  >
                    <span className="flex-1 text-sm">{t(link.text)}</span>
                    <span className="text-gray-400 transition-colors group-hover:text-white">
                      →
                    </span>
                  </Link>
                </li>
              ))}
          </ul>

          {/* Quick Actions - Compact */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Link
              to="/donate"
              onClick={toggleNavbar}
              className="flex items-center justify-center space-x-1.5 rounded-xl bg-gradient-to-r from-[rgb(210,166,73)] to-[rgb(246,203,110)] px-3 py-2.5 text-center text-xs font-bold text-[rgb(4,50,75)] shadow-md"
            >
              <FiHeart className="h-4 w-4" />
              <span>{t("navbar.donate")}</span>
            </Link>
            <Link
              to="/get-involved"
              onClick={toggleNavbar}
              className="flex items-center justify-center space-x-1.5 rounded-xl bg-[rgb(4,50,75)] px-3 py-2.5 text-center text-xs font-bold text-white shadow-md hover:bg-[rgb(29,84,114)]"
            >
              <FiUsers className="h-4 w-4" />
              <span>{t("navbar.volunteer")}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer - Compact */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-2">
        <p className="text-center text-[10px] text-gray-500">© 2024 ACDHA</p>
      </div>
    </div>
  );
}
